import React, { useState } from 'react';
import {
  Box,
  VStack,
  Heading,
  Button,
  Card,
  CardBody,
  Text,
  Badge,
  Icon,
  useColorModeValue,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Progress,
  Flex,
  Divider,
  List,
  ListItem,
  ListIcon,
  FormHelperText,
} from '@chakra-ui/react';
import {
  FiDownload,
  FiDollarSign,
  FiMoreVertical,
  FiCheckCircle,
  FiAlertCircle,
  FiClock,
  FiCalendar,
  FiFileText,
  FiPlus,
  FiCreditCard,
  FiSettings,
} from 'react-icons/fi';

const Billing = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Dummy data for demonstration
  const billingStats = {
    currentBalance: 1250.00,
    monthlyUsage: 850.00,
    remainingCredits: 400.00,
    nextBillingDate: '2024-04-01',
  };

  const invoices = [
    {
      id: 'INV-001',
      date: '2024-03-01',
      amount: 1250.00,
      status: 'Paid',
      dueDate: '2024-03-15',
    },
    {
      id: 'INV-002',
      date: '2024-02-01',
      amount: 1150.00,
      status: 'Paid',
      dueDate: '2024-02-15',
    },
  ];

  const usageDetails = [
    {
      service: 'Voice Calls',
      usage: '1,250 minutes',
      cost: 625.00,
      limit: '2,000 minutes',
    },
    {
      service: 'SMS',
      usage: '500 messages',
      cost: 125.00,
      limit: '1,000 messages',
    },
    {
      service: 'Storage',
      usage: '25 GB',
      cost: 100.00,
      limit: '50 GB',
    },
  ];

  const calculateProgressValue = (usage, limit) => {
    const usageValue = parseInt(usage.replace(/[^0-9]/g, ''));
    const limitValue = parseInt(limit.replace(/[^0-9]/g, ''));
    return (usageValue / limitValue) * 100;
  };

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Heading size="lg">Billing & Usage</Heading>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="purple"
            onClick={onOpen}
          >
            Add Payment Method
          </Button>
        </Box>

        {/* Stats Cards */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Current Balance</StatLabel>
                <StatNumber>${billingStats.currentBalance.toFixed(2)}</StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  12.5% from last month
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Monthly Usage</StatLabel>
                <StatNumber>${billingStats.monthlyUsage.toFixed(2)}</StatNumber>
                <StatHelpText>This month</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Remaining Credits</StatLabel>
                <StatNumber>${billingStats.remainingCredits.toFixed(2)}</StatNumber>
                <StatHelpText>Available balance</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Next Billing Date</StatLabel>
                <StatNumber>{billingStats.nextBillingDate}</StatNumber>
                <StatHelpText>Auto-pay enabled</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Usage Details */}
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="md" mb={4}>Usage Details</Heading>
            <VStack spacing={4} align="stretch">
              {usageDetails.map((item, index) => (
                <Box key={index}>
                  <Flex justifyContent="space-between" mb={2}>
                    <Text fontWeight="medium">{item.service}</Text>
                    <Text>${item.cost.toFixed(2)}</Text>
                  </Flex>
                  <Progress
                    value={calculateProgressValue(item.usage, item.limit)}
                    size="sm"
                    colorScheme="purple"
                    mb={2}
                  />
                  <Flex justifyContent="space-between" fontSize="sm" color="gray.500">
                    <Text>{item.usage} used</Text>
                    <Text>{item.limit} limit</Text>
                  </Flex>
                </Box>
              ))}
            </VStack>
          </CardBody>
        </Card>

        {/* Invoices Table */}
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="md" mb={4}>Recent Invoices</Heading>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Invoice ID</Th>
                  <Th>Date</Th>
                  <Th>Amount</Th>
                  <Th>Status</Th>
                  <Th>Due Date</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {invoices.map((invoice) => (
                  <Tr key={invoice.id}>
                    <Td>{invoice.id}</Td>
                    <Td>{invoice.date}</Td>
                    <Td>${invoice.amount.toFixed(2)}</Td>
                    <Td>
                      <Badge
                        colorScheme={invoice.status === 'Paid' ? 'green' : 'yellow'}
                      >
                        {invoice.status}
                      </Badge>
                    </Td>
                    <Td>{invoice.dueDate}</Td>
                    <Td>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<FiMoreVertical />}
                          variant="ghost"
                          size="sm"
                        />
                        <MenuList>
                          <MenuItem icon={<FiDownload />}>Download PDF</MenuItem>
                          <MenuItem icon={<FiFileText />}>View Details</MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </VStack>

      {/* Add Payment Method Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Payment Method</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Card Number</FormLabel>
                <Input placeholder="1234 5678 9012 3456" />
              </FormControl>

              <SimpleGrid columns={2} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Expiry Date</FormLabel>
                  <Input placeholder="MM/YY" />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>CVV</FormLabel>
                  <Input placeholder="123" type="password" />
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired>
                <FormLabel>Name on Card</FormLabel>
                <Input placeholder="John Doe" />
              </FormControl>

              <FormControl>
                <FormLabel>Billing Address</FormLabel>
                <Input placeholder="123 Main St" />
              </FormControl>

              <Box w="full" display="flex" justifyContent="flex-end" gap={3}>
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button colorScheme="purple">
                  Add Card
                </Button>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Billing;
